import { useTranslation } from 'react-i18next';
import {
  AlgComponentType,
  UserRequirementInclusionType,
  UserRequirementNumUsersType,
  UserRequirementType,
  User,
  UserReqRule,
  UserRequirement,
} from '@youshift/shared/types';
import {
  dateToString,
  formatUserName,
  returnColor,
} from '@youshift/shared/utils';
import { useState, Fragment, useMemo } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  UserIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { ComponentPrioritySchema } from '@youshift/shared/hooks/mutations';

import InclusionTypeBadge from '../../pages/Manager/IterationConfig/UserReqRules/InclusionTypeBadge';
import ImportanceInput from './ImportanceInput';
import { RuleAttributes } from '../../pages/Manager/IterationConfig/UserReqRules/hooks/useClassifyRule';
import { AlgComponentTypeSupportingPriority } from './types';

interface UserReqPriorityPerRuleProps {
  allUserReqRules: Record<number, UserReqRule>;
  ruleAttributes: Record<number, RuleAttributes>;
  users: Record<number, User>;
  importanceValues: ComponentPrioritySchema;
  setImportanceValues: (
    fn: (prev: ComponentPrioritySchema) => ComponentPrioritySchema,
  ) => void;
  getRequirementTranslationKey: (
    reqType: UserRequirementType,
    isMin: boolean,
  ) => string;
  getUserReqUnitTranslationKey: (userReq: UserRequirement) => string;
  getMinComponentType: (
    userReq: UserRequirement,
  ) => AlgComponentTypeSupportingPriority;
  getMaxComponentType: (
    userReq: UserRequirement,
  ) => AlgComponentTypeSupportingPriority;
  getReqValue: (
    componentType: AlgComponentType,
    userReq: UserRequirement,
  ) => number | null;
}

export default function UserReqPriorityPerRule({
  allUserReqRules,
  ruleAttributes,
  users,
  importanceValues,
  setImportanceValues,
  getRequirementTranslationKey,
  getUserReqUnitTranslationKey,
  getMinComponentType,
  getMaxComponentType,
  getReqValue,
}: UserReqPriorityPerRuleProps) {
  const { t } = useTranslation();

  const [expandedRules, setExpandedRules] = useState<Set<number>>(new Set());

  // Calculate rule-level priorities based on user requirements
  const computeRuleModeImportance = useMemo(
    () =>
      Object.entries(allUserReqRules).reduce(
        (acc, [ruleId, rule]) => {
          // Calculate mode for min component type
          const minValues = Object.values(rule.user_reqs)
            .map(req => {
              const componentType = getMinComponentType(req);
              return importanceValues[componentType][req.id_user_requirement];
            })
            .filter(value => value !== undefined) as number[];

          const minMode =
            minValues.length > 0
              ? Number(
                  Object.entries(
                    minValues.reduce(
                      (freq, val) => {
                        freq[val] = (freq[val] || 0) + 1;
                        return freq;
                      },
                      {} as Record<number, number>,
                    ),
                  ).reduce((a, b) => (a[1] > b[1] ? a : b))[0],
                )
              : 0;

          // Calculate mode for max component type
          const maxValues = Object.values(rule.user_reqs)
            .map(req => {
              const componentType = getMaxComponentType(req);
              return importanceValues[componentType][req.id_user_requirement];
            })
            .filter(value => value !== undefined) as number[];

          const maxMode =
            maxValues.length > 0
              ? Number(
                  Object.entries(
                    maxValues.reduce(
                      (freq, val) => {
                        freq[val] = (freq[val] || 0) + 1;
                        return freq;
                      },
                      {} as Record<number, number>,
                    ),
                  ).reduce((a, b) => (a[1] > b[1] ? a : b))[0],
                )
              : 0;

          acc[Number(ruleId)] = {
            min: minMode,
            max: maxMode,
          };
          return acc;
        },
        {} as Record<number, { min: number; max: number }>,
      ),
    [
      allUserReqRules,
      importanceValues,
      getMinComponentType,
      getMaxComponentType,
    ],
  );

  const toggleRule = (ruleId: number) => {
    setExpandedRules((prev: Set<number>) => {
      const newSet = new Set<number>(prev);
      if (newSet.has(ruleId)) {
        newSet.delete(ruleId);
      } else {
        newSet.add(ruleId);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-2">
      {/* Multiple user rules */}
      {Object.entries(allUserReqRules)
        .filter(
          ([ruleId, rule]) =>
            ruleAttributes[Number(ruleId)].numUsersType ===
            UserRequirementNumUsersType.MULTIPLE,
        )
        .sort(([ruleIdA, ruleA], [ruleIdB, ruleB]) => {
          const typeA = ruleAttributes[Number(ruleIdA)].inclusionType;
          const typeB = ruleAttributes[Number(ruleIdB)].inclusionType;
          if (typeA !== typeB) {
            return typeA === UserRequirementInclusionType.INCLUSION ? -1 : 1;
          }
          return ruleA.rule.name.localeCompare(ruleB.rule.name);
        })
        .map(([ruleId, rule]) => {
          const isExpanded = expandedRules.has(Number(ruleId));
          const userReqs = Object.values(rule.user_reqs);
          if (userReqs.length === 0) return null;

          const firstUserReq = userReqs[0];
          const ruleUnit = getUserReqUnitTranslationKey(firstUserReq);
          const minComponentType = getMinComponentType(firstUserReq);
          const maxComponentType = getMaxComponentType(firstUserReq);

          return (
            <div key={ruleId} className="border rounded">
              <button
                onClick={() => toggleRule(Number(ruleId))}
                className="flex items-center justify-between w-full text-left p-2 hover:bg-gray-50"
                style={{
                  borderLeft: `4px solid ${returnColor('blue')}`,
                  backgroundColor: returnColor('blue', 50),
                  color: returnColor('blue', 800),
                }}
              >
                <div className="grid grid-cols-[1fr,2fr,auto] w-full">
                  <div className="flex items-center gap-2">
                    <InclusionTypeBadge
                      label={ruleAttributes[Number(ruleId)].inclusionType}
                    />
                    <div className="flex items-center gap-1">
                      {ruleAttributes[Number(ruleId)].numUsersType ===
                      UserRequirementNumUsersType.SINGLE ? (
                        <UserIcon className="h-4 w-4 text-gray-500" />
                      ) : (
                        <UserGroupIcon className="h-4 w-4 text-gray-500" />
                      )}
                    </div>
                    <span className="text-sm font-medium">
                      {rule.rule.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({t(ruleUnit)})
                    </span>
                  </div>
                  <div className="flex justify-center">
                    <table className="border-collapse">
                      <tbody>
                        {ruleAttributes[Number(ruleId)].inclusionType ===
                          UserRequirementInclusionType.INCLUSION && (
                          <tr>
                            <td className="pr-2">
                              <span className="text-sm text-gray-600">
                                {t(
                                  getRequirementTranslationKey(
                                    Object.values(rule.user_reqs)[0]?.req_type,
                                    true,
                                  ),
                                )}
                              </span>
                            </td>
                            <td className="pr-2">
                              <ImportanceInput
                                value={
                                  computeRuleModeImportance[Number(ruleId)]
                                    ?.min ?? 0
                                }
                                onChange={value => {
                                  // Update all user requirements in this rule
                                  Object.values(rule.user_reqs).forEach(
                                    userReq => {
                                      setImportanceValues(prev => ({
                                        ...prev,
                                        [minComponentType]: {
                                          ...prev[minComponentType],
                                          [userReq.id_user_requirement]: value,
                                        },
                                      }));
                                    },
                                  );
                                }}
                              />
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td className="pr-2">
                            <span className="text-sm text-gray-600">
                              {t(
                                getRequirementTranslationKey(
                                  Object.values(rule.user_reqs)[0]?.req_type,
                                  false,
                                ),
                              )}
                            </span>
                          </td>
                          <td className="pr-2">
                            <ImportanceInput
                              value={
                                computeRuleModeImportance[Number(ruleId)]
                                  ?.max ?? 0
                              }
                              onChange={value => {
                                // Update all user requirements in this rule
                                Object.values(rule.user_reqs).forEach(
                                  userReq => {
                                    setImportanceValues(prev => ({
                                      ...prev,
                                      [maxComponentType]: {
                                        ...prev[maxComponentType],
                                        [userReq.id_user_requirement]: value,
                                      },
                                    }));
                                  },
                                );
                              }}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-end">
                    {isExpanded ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </div>
                </div>
              </button>
              {isExpanded && (
                <div className="p-2 space-y-2">
                  {Object.values(rule.user_reqs)
                    .sort((a, b) => {
                      const userA = users[a.id_user];
                      const userB = users[b.id_user];
                      const firstNameCompare = userA?.firstname.localeCompare(
                        userB?.firstname,
                      );
                      return firstNameCompare === 0
                        ? userA?.lastname.localeCompare(userB?.lastname)
                        : firstNameCompare;
                    })
                    .map(userReq => {
                      const minComponentType = getMinComponentType(userReq);
                      const maxComponentType = getMaxComponentType(userReq);

                      const minReqValue = getReqValue(
                        minComponentType,
                        userReq,
                      );
                      const maxReqValue = getReqValue(
                        maxComponentType,
                        userReq,
                      );
                      const userReqMinImportance =
                        importanceValues[minComponentType][
                          userReq.id_user_requirement
                        ];
                      const userReqMaxImportance =
                        importanceValues[maxComponentType][
                          userReq.id_user_requirement
                        ];
                      return (
                        <div
                          key={userReq.id_user_requirement}
                          className="grid grid-cols-[1fr,1.5fr,1.5fr] gap-x-4 items-center hover:bg-gray-50 p-2 rounded"
                        >
                          <div className="text-sm text-gray-600">
                            <div className="flex flex-col gap-1">
                              <span className="text-sm font-medium">
                                {formatUserName(users[userReq.id_user], false)}
                              </span>
                              <div className="flex items-center gap-2">
                                <InclusionTypeBadge
                                  label={
                                    ruleAttributes[Number(ruleId)].inclusionType
                                  }
                                />
                                {ruleAttributes[Number(ruleId)].numUsersType ===
                                UserRequirementNumUsersType.SINGLE ? (
                                  <UserIcon className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <UserGroupIcon className="h-4 w-4 text-gray-500" />
                                )}
                                <span className="text-xs text-gray-500">
                                  ({t(getUserReqUnitTranslationKey(userReq))})
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-4">
                            {ruleAttributes[Number(ruleId)].inclusionType ===
                              UserRequirementInclusionType.INCLUSION && (
                              <span className="text-sm">
                                {t(
                                  getRequirementTranslationKey(
                                    Object.values(rule.user_reqs)[0]?.req_type,
                                    true,
                                  ),
                                )}
                                :{' '}
                                <span className="font-bold">{minReqValue}</span>
                              </span>
                            )}
                            <span className="text-sm">
                              {t(
                                getRequirementTranslationKey(
                                  Object.values(rule.user_reqs)[0]?.req_type,
                                  false,
                                ),
                              )}
                              : <span className="font-bold">{maxReqValue}</span>
                            </span>
                          </div>
                          <div className="flex flex-col gap-2">
                            {ruleAttributes[Number(ruleId)].inclusionType ===
                              UserRequirementInclusionType.INCLUSION && (
                              <div className="flex items-center gap-2">
                                <ImportanceInput
                                  value={userReqMinImportance}
                                  onChange={value => {
                                    setImportanceValues(
                                      (prev: ComponentPrioritySchema) => ({
                                        ...prev,
                                        [minComponentType]: {
                                          ...prev[minComponentType],
                                          [userReq.id_user_requirement]: value,
                                        },
                                      }),
                                    );
                                  }}
                                />
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <ImportanceInput
                                value={userReqMaxImportance}
                                onChange={value => {
                                  setImportanceValues(
                                    (prev: ComponentPrioritySchema) => ({
                                      ...prev,
                                      [maxComponentType]: {
                                        ...prev[maxComponentType],
                                        [userReq.id_user_requirement]: value,
                                      },
                                    }),
                                  );
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
}
